import { Card } from '@/components/design-system/Card';
import { Check, ChevronRight } from 'lucide-react';
import {
  FormulaProduct,
  useGetFormulaBenefits,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { useFindPlanBySkuSet } from '@viome/viome-base/src/usecase/Shop/ShopUtil';
import { Checkbox } from '@/components/design-system/Checkbox';
import { RadioGroupItem } from '@/components/design-system/radio-group';
import { cn } from '@/utils/styles';
import { useNavigate } from 'react-router-dom';
import { constructFormulaBenefitsText } from '@screens/ShopPage/shopPageFunctions';
import {
  formatPackPrice,
  getDiscountedPrice,
} from '@screens/ShopPage/utils/pricing';
import { StrikethroughPrice } from './StrikethroughPrice';
import { Skeleton } from '@design-system/Skeleton';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';

interface ProductCardProps {
  formula: FormulaProduct;
  isSelected?: boolean;
  onToggleSelected?: (productId: string) => void;
  isEditable?: boolean;
  cardStyle?: 'default' | 'subscribedHighlight';
  selectionMode?: 'checkbox' | 'radio';
  disabled?: boolean;
  showPrice?: boolean;
  discountPercentage?: number;
}

export const ProductCard = ({
  formula,
  isSelected = true,
  onToggleSelected,
  isEditable = false,
  cardStyle = 'default',
  selectionMode = 'checkbox',
  disabled = false,
  showPrice = false,
  discountPercentage = 0,
}: ProductCardProps) => {
  const navigate = useNavigate();
  const {
    data: formulaBenefitsData,
    isLoading,
    isError,
  } = useGetFormulaBenefits(Number(formula?.productId));
  const { plan } = useFindPlanBySkuSet(formula?.sku ? [formula.sku] : []);
  const benefitsText = constructFormulaBenefitsText(formulaBenefitsData);
  const basePrice = plan?.plan_product?.price
    ? parseFloat(plan.plan_product.price)
    : undefined;
  const monthlyPrice =
    basePrice !== undefined
      ? getDiscountedPrice(basePrice, 0, discountPercentage).discountedPrice
      : undefined;

  const handleCardClick = () => {
    if (disabled) return;

    if (!isEditable) {
      navigate(`/shop/product/${formula?.product_id}`);
    } else if (onToggleSelected) {
      onToggleSelected(String(formula.product_id));
    }

    // analytics tracking events
    if (formula?.productId === '1') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_MYFORMULAS_ORALLOZENGES,
      );
    } else if (formula?.productId === '2') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_MYFORMULAS_SUPPLEMENTS,
      );
    } else if (formula?.productId === '3') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_MYFORMULAS_GUTFORMULA,
      );
    } else if (formula?.productId === '4') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_MYFORMULAS_TOOTHPASTEANDGEL,
      );
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled) return;
    if (onToggleSelected) {
      onToggleSelected(String(formula.product_id));
    }
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/shop/product/${formula.product_id}`);
  };

  return (
    <Card
      className={cn(
        'mb-3 rounded-xl p-4 flex gap-3 transition-all relative justify-between',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-lg',
        isEditable && isSelected && !disabled && 'border-2 border-[#146464]',
        isEditable && !isSelected && !disabled && 'border border-gray-300',
        isEditable && disabled && 'border border-gray-200',
        !isEditable && 'border border-gray-200 hover:border-gray-400',
        cardStyle === 'subscribedHighlight' && 'bg-background-tertiary',
        cardStyle === 'default' && 'bg-white',
      )}
      onClick={handleCardClick}
    >
      <div className="flex flex-col items-start gap-3 w-full">
        {!formula?.is_subscribed && formula?.is_retest_product && (
          <div className="typography-body text-secondary bg-background-tertiary py-1 px-4 rounded w-fit mx-auto sm:mx-0">
            Now available with your latest test
          </div>
        )}
        <div className="flex w-full gap-3">
          {formula.productImageUrl && (
            <div className="flex-shrink-0">
              <img
                src={formula.productImageUrl}
                alt={formula.title}
                className="h-12 w-12 object-contain"
              />
            </div>
          )}
          <div className="flex flex-wrap flex-1 min-w-0 gap-x-3 gap-y-2">
            <div className="flex flex-col flex-1 min-w-[8rem]">
              <h3 className="typography-h3 text-primary font-medium mb-1">
                {formula.title}
              </h3>
              <div
                style={{ color: formula?.color }}
                className="typography-btn-medium font-serif italic mb-1"
              >
                {formula.ingredients_count} ingredients
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0 ml-auto">
              {showPrice && monthlyPrice !== undefined && (
                <div className="flex flex-col items-end text-xs">
                  {discountPercentage > 0 && basePrice !== undefined && (
                    <StrikethroughPrice>
                      {formatPackPrice(basePrice, false)}/mo
                    </StrikethroughPrice>
                  )}
                  <span className="text-[16px] font-medium text-[#146464] whitespace-nowrap">
                    {formatPackPrice(monthlyPrice, false)}/mo
                  </span>
                </div>
              )}
              <div className="flex items-center shrink-0">
                {isEditable ? (
                  selectionMode === 'radio' ? (
                    <RadioGroupItem
                      value={String(formula.product_id)}
                      onClick={handleCheckboxClick}
                      disabled={disabled}
                      className="size-4 self-center border-2 bg-white data-[state=checked]:bg-white data-[state=unchecked]:border-gray-400 data-[state=checked]:border-[#146464]"
                      indicatorClassName="bg-[#146464]"
                      aria-label={`Select ${formula.title}`}
                    />
                  ) : (
                    <Checkbox
                      checked={isSelected}
                      onClick={handleCheckboxClick}
                      disabled={disabled}
                      className="size-4 self-center rounded-[4px] border-2 data-[state=checked]:bg-[#146464] data-[state=unchecked]:border-gray-400 data-[state=checked]:border-[#146464]"
                      aria-label={`Select ${formula.title}`}
                    />
                  )
                ) : (
                  cardStyle !== 'subscribedHighlight' &&
                  isSelected && <Check className="w-5 h-5 text-[#009E82]" />
                )}
              </div>
            </div>
          </div>
        </div>
        {!isError && (
          <>
            {isLoading ? (
              <Skeleton className="h-12 bg-gray-100 rounded w-full z-50" />
            ) : benefitsText ? (
              <p className="typography-body text-secondary">{benefitsText}</p>
            ) : null}
          </>
        )}
        {showPrice && (
          <button
            type="button"
            className="flex items-center self-start typography-btn-medium text-[#146464]"
            onClick={handleLearnMoreClick}
            data-testid={`shopFormulas-learnMore-${formula.product_id}`}
          >
            Learn More
            <ChevronRight className="h-4 w-4 ml-0.5" />
          </button>
        )}
      </div>
    </Card>
  );
};
